import {
    onBeforeUnmount,
    onMounted,
    type Ref
} from "vue";


//////////////////////////////////////////////////
//  PUBLIC REVEAL
//////////////////////////////////////////////////


const RevealSelector = "[data-reveal]";
const RevealVisibleClass = "is-revealed";
const RevealDelayProperty = "--reveal-delay";
const RevealThresholdAttribute = "data-reveal-threshold";
const RevealRootMarginAttribute = "data-reveal-root-margin";
const DefaultRevealThreshold = .16;
const DefaultRevealRootMargin = "0px 0px -10% 0px";


type RevealObserverConfig = {
    rootMargin : string;
    threshold  : number;
};


function readRevealThreshold(element: HTMLElement) {
    const rawThreshold = element.getAttribute(RevealThresholdAttribute);

    if (rawThreshold === null)
        return DefaultRevealThreshold;

    const threshold = Number(rawThreshold);

    if (!Number.isFinite(threshold))
        return DefaultRevealThreshold;

    return Math.min(Math.max(threshold, 0), 1);
}

function readRevealRootMargin(element: HTMLElement) {
    return element.getAttribute(RevealRootMarginAttribute) || DefaultRevealRootMargin;
}

function getRevealObserverKey(config: RevealObserverConfig) {
    return `${config.rootMargin}:${config.threshold}`;
}


function revealElement(element: HTMLElement) {
    element.classList.add(RevealVisibleClass);
}

function setRevealDelay(element: HTMLElement, index: number) {
    if (element.style.getPropertyValue(RevealDelayProperty)) {
        return;
    }

    element.style.setProperty(RevealDelayProperty, `${Math.min(index * 55, 280)}ms`);
}

function shouldSkipMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function usePublicReveal(root: Ref<HTMLElement | null>) {
    let mutationObserver: MutationObserver | null = null;
    const observers = new Map<string, IntersectionObserver>();
    const observedElements = new WeakSet<HTMLElement>();

    const getObserver = (config: RevealObserverConfig) => {
        const key = getRevealObserverKey(config);
        const existingObserver = observers.get(key);

        if (existingObserver)
            return existingObserver;

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting)
                    continue;

                const element = entry.target;

                if (element instanceof HTMLElement) {
                    revealElement(element);
                    observer.unobserve(element);
                }
            }
        }, {
            rootMargin : config.rootMargin,
            threshold  : config.threshold,
        });

        observers.set(key, observer);
        return observer;
    };

    const observeRevealElements = (rootElement: HTMLElement) => {
        const elements = Array.from(rootElement.querySelectorAll<HTMLElement>(RevealSelector));

        elements.forEach((element, index) => {
            if (observedElements.has(element) || element.classList.contains(RevealVisibleClass))
                return;

            observedElements.add(element);
            setRevealDelay(element, index);
            getObserver({
                rootMargin : readRevealRootMargin(element),
                threshold  : readRevealThreshold(element),
            }).observe(element);
        });
    };

    onMounted(() => {
        const rootElement = root.value;

        if (!rootElement) {
            return;
        }

        const elements = Array.from(rootElement.querySelectorAll<HTMLElement>(RevealSelector));

        if (!("IntersectionObserver" in window) || shouldSkipMotion()) {
            elements.forEach(revealElement);
            return;
        }

        observeRevealElements(rootElement);

        mutationObserver = new MutationObserver(() => {
            observeRevealElements(rootElement);
        });

        mutationObserver.observe(rootElement, {
            childList : true,
            subtree   : true,
        });
    });

    onBeforeUnmount(() => {
        for (const observer of observers.values())
            observer.disconnect();

        observers.clear();
        mutationObserver?.disconnect();
        mutationObserver = null;
    });
}
